import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

type Status = 'pending' | 'approved' | 'executing' | 'succeeded' | 'failed' | 'rejected';

interface WriteTask {
  id: number;
  taskType: string;
  payload: unknown;
  status: Status;
  idempotencyKey: string;
  createdAt: string;
  approvedAt: string | null;
  executedAt: string | null;
  attempts: number;
  lastError: string | null;
}

interface ListResponse {
  rows: WriteTask[];
}

const STATUSES: Array<Status | 'all'> = [
  'pending',
  'approved',
  'executing',
  'succeeded',
  'failed',
  'rejected',
  'all',
];

export function AdminWriteTasksPage() {
  const [filter, setFilter] = useState<Status | 'all'>('pending');
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'write-tasks', filter],
    queryFn: () =>
      apiFetch<ListResponse>(
        `/api/admin/write-tasks${filter === 'all' ? '' : `?status=${filter}`}`,
      ),
    refetchInterval: 30_000,
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'approve' | 'reject' }) =>
      apiFetch(`/api/admin/write-tasks/${id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'write-tasks'] }),
  });

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Write tasks</h1>
        <span className="main-subtitle">
          OBM writes queued from SmartLink scan results · refreshes every 30s
        </span>
      </header>
      <Link to="/admin" className="back-link">← Admin</Link>

      <SimulatePanel onFired={() => qc.invalidateQueries({ queryKey: ['admin', 'write-tasks'] })} />

      <div className="toolbar" style={{ gap: '0.3rem' }}>
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            style={{
              background: filter === s ? 'var(--accent)' : 'var(--surface-2)',
              color: filter === s ? 'var(--bg)' : 'var(--text)',
              border: '1px solid var(--border)',
              padding: '0.35rem 0.8rem',
              cursor: 'pointer',
              fontSize: 12,
              borderRadius: 2,
              fontWeight: filter === s ? 600 : 400,
            }}
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <section className="panel">
          <header className="panel-header">
            <h2 className="panel-title">Tasks</h2>
            <span className="panel-subtitle">{data.rows.length} shown</span>
          </header>
          {data.rows.length === 0 ? (
            <div className="empty">
              No tasks with status <code>{filter}</code>. SmartLink scan
              callbacks land here as <code>pending</code>.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Approved</th>
                  <th>Executed</th>
                  <th className="num">Attempts</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    onAction={(action) => act.mutate({ id: t.id, action })}
                    busy={act.isPending}
                  />
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </>
  );
}

function TaskRow({
  task,
  onAction,
  busy,
}: {
  task: WriteTask;
  onAction: (a: 'approve' | 'reject') => void;
  busy: boolean;
}) {
  const orderNo =
    (task.payload as { orderNo?: string } | null)?.orderNo ?? '—';
  return (
    <tr>
      <td className="mono" style={{ fontSize: 11 }}>{task.id}</td>
      <td className="mono">{task.taskType}</td>
      <td className="mono">{orderNo}</td>
      <td>
        <span className={`badge ${statusBadge(task.status)}`}>{task.status}</span>
      </td>
      <td className="mono" style={{ fontSize: 11 }}>{dateTime(task.createdAt)}</td>
      <td className="mono" style={{ fontSize: 11 }}>
        {task.approvedAt ? dateTime(task.approvedAt) : '—'}
      </td>
      <td className="mono" style={{ fontSize: 11 }}>
        {task.executedAt ? dateTime(task.executedAt) : '—'}
      </td>
      <td className="num">{task.attempts}</td>
      <td>
        {task.status === 'pending' ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => onAction('approve')}
              disabled={busy}
              style={btnApprove}
            >
              Approve
            </button>
            <button
              onClick={() => onAction('reject')}
              disabled={busy}
              style={btnReject}
            >
              Reject
            </button>
          </div>
        ) : task.lastError ? (
          <span style={{ fontSize: 11, color: 'var(--danger, #ff6b6b)' }} title={task.lastError}>
            error
          </span>
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}

function SimulatePanel({ onFired }: { onFired: () => void }) {
  const [direction, setDirection] = useState<'warehouse-outbound' | 'warehouse-inbound'>(
    'warehouse-outbound',
  );
  const [body, setBody] = useState(
    JSON.stringify(
      {
        orderNo: 'DO26/0309',
        submitDate: '2026-05-16 10:00:00',
        linkOrderNo: '',
        realQty: 2,
        data: [{ sku: 'VB41986101', qty: 2, realQty: 2 }],
      },
      null,
      2,
    ),
  );
  const [result, setResult] = useState<unknown>(null);
  const fire = useMutation({
    mutationFn: () => {
      let parsedBody: unknown;
      try {
        parsedBody = JSON.parse(body);
      } catch (e) {
        throw new Error('Body is not valid JSON.');
      }
      return apiFetch('/api/admin/simulate-scan-result', {
        method: 'POST',
        body: JSON.stringify({ direction, body: parsedBody }),
      });
    },
    onSuccess: (r) => {
      setResult(r);
      onFired();
    },
    onError: (e) => setResult({ error: (e as Error).message }),
  });
  return (
    <section className="panel">
      <header className="panel-header">
        <h2 className="panel-title">Simulate a SmartLink scan result</h2>
        <span className="panel-subtitle">runs the same processor the webhook does</span>
      </header>
      <div style={{ display: 'grid', gap: '0.6rem' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['warehouse-outbound', 'warehouse-inbound'] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDirection(d)}
              style={{
                background: direction === d ? 'var(--accent)' : 'var(--surface-2)',
                color: direction === d ? 'var(--bg)' : 'var(--text)',
                border: '1px solid var(--border)',
                padding: '0.35rem 0.8rem',
                cursor: 'pointer',
                fontSize: 12,
                borderRadius: 2,
              }}
            >
              {d}
            </button>
          ))}
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
        />
        <div>
          <button
            type="button"
            onClick={() => fire.mutate()}
            disabled={fire.isPending}
            style={{
              background: 'var(--accent)',
              color: 'var(--bg)',
              border: 'none',
              padding: '0.5rem 1rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {fire.isPending ? 'Processing…' : 'Simulate'}
          </button>
        </div>
        {result != null && (
          <pre
            style={{
              background: 'var(--surface-2)',
              padding: '0.6rem',
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              maxHeight: 180,
              overflow: 'auto',
              margin: 0,
              border: '1px solid var(--border)',
            }}
          >
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    </section>
  );
}

function statusBadge(s: Status): string {
  if (s === 'succeeded') return 'badge-ok';
  if (s === 'failed' || s === 'rejected') return 'badge-cancel';
  if (s === 'pending' || s === 'approved' || s === 'executing') return 'badge-pending';
  return 'badge-neutral';
}

const btnApprove: React.CSSProperties = {
  background: 'var(--accent)',
  color: 'var(--bg)',
  border: 'none',
  padding: '0.3rem 0.7rem',
  cursor: 'pointer',
  fontSize: 11,
  borderRadius: 2,
};

const btnReject: React.CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  padding: '0.3rem 0.7rem',
  cursor: 'pointer',
  fontSize: 11,
  borderRadius: 2,
};
